import { z } from "zod";

/** Archetypes come in boy/girl pairs per family (see `ARCHETYPE_FAMILIES` in content). */
export const AVATAR_IDS = [
  "wizard",
  "sorceress",
  "princess",
  "prince",
  "angel-boy",
  "angel-girl",
  "demon-boy",
  "demon-girl",
] as const;
export const avatarIdSchema = z.enum(AVATAR_IDS);
export type AvatarId = z.infer<typeof avatarIdSchema>;

export const EQUIPMENT_SLOTS = [
  "head",
  "hair_accessory",
  "face",
  "neck",
  "back",
  "hand",
  "aura",
] as const;
export const equipmentSlotSchema = z.enum(EQUIPMENT_SLOTS);
export type EquipmentSlot = z.infer<typeof equipmentSlotSchema>;

export const AVATAR_POSES = ["front", "back", "side"] as const;
export const avatarPoseSchema = z.enum(AVATAR_POSES);
export type AvatarPose = z.infer<typeof avatarPoseSchema>;

export const cosmeticIdSchema = z
  .string()
  .min(3)
  .max(48)
  .regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/);

/** slot -> item id. Missing slot means nothing equipped there. */
export const equipmentSchema = z
  .object({
    head: cosmeticIdSchema.optional(),
    hair_accessory: cosmeticIdSchema.optional(),
    face: cosmeticIdSchema.optional(),
    neck: cosmeticIdSchema.optional(),
    back: cosmeticIdSchema.optional(),
    hand: cosmeticIdSchema.optional(),
    aura: cosmeticIdSchema.optional(),
  })
  .strict();
export type Equipment = z.infer<typeof equipmentSchema>;

export const anchorSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  scale: z.number().positive(),
  rotation: z.number().finite().optional(),
});
export type Anchor = z.infer<typeof anchorSchema>;

export const cosmeticItemSchema = z.object({
  id: cosmeticIdSchema,
  name: z.string().min(1).max(40),
  slot: equipmentSlotSchema,
  priceGold: z.number().int().nonnegative(),
  rarity: z.enum(["common", "rare", "epic"]),
  /** pose -> asset id. `front` is required; other poses fall back to it. */
  assetIds: z.object({ front: z.string().min(1) }).catchall(z.string().min(1)),
  allowedArchetypes: z.union([z.array(avatarIdSchema).min(1), z.literal("all")]),
  /** Per-item fine tuning on top of the archetype slot anchor, by pose. */
  anchorsByPose: z.record(avatarPoseSchema, anchorSchema),
  collection: z.string().min(1),
  isAvailable: z.boolean(),
  /** Starter items are owned by every player for free. */
  starter: z.boolean().default(false),
  /** Hidden in poses where the slot faces away (e.g. glasses from behind). */
  hiddenInPoses: z.array(avatarPoseSchema).default([]),
});
export type CosmeticItem = z.infer<typeof cosmeticItemSchema>;
export type CosmeticItemInput = z.input<typeof cosmeticItemSchema>;
